import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

/**
 * Yurest Custom PrimeNG Preset
 *
 * This preset extends Aura and integrates design tokens from the yukit package.
 * Design tokens are organized in 3 tiers:
 * - Primitive: Raw values like color palettes (no context)
 * - Semantic: Contextual tokens like primary.color, surface.ground
 * - Component: Component-specific tokens like button.background
 *
 * @see https://primeng.org/theming/styled
 * @see yukit tokens: node_modules/yukit/src/lib/theme/tokens/
 */
export const YurestPreset = definePreset(Aura, {
  // ============================================================================
  // PRIMITIVE TOKENS
  // Raw values from yukit tokens
  // ============================================================================
  primitive: {
    // Border radius from yukit radius.scss
    borderRadius: {
      none: '0',
      xs: '2px',
      sm: '4px', // --layout-radius-component-small
      md: '6px', // --layout-radius-component-regular
      lg: '8px', // --layout-radius-component-medium
      xl: '16px', // --layout-radius-component-large
    },

    // Green palette from yukit success colors
    green: {
      50: '#ECFDF0', // --success-lighter-ext
      100: '#D1FAE5',
      200: '#A7F3D0',
      300: '#6EE7B7',
      400: '#34D399', // --success-light
      500: '#10B981', // --success
      600: '#028E5C', // --success-dark
      700: '#047857',
      800: '#065F46',
      900: '#064E3B',
      950: '#022C22', // --success-dark-ext
    },

    // Blue palette from yukit info colors
    blue: {
      50: '#EFF6FF', // --info-lighter-ext
      100: '#DBEAFE',
      200: '#BFDBFE',
      300: '#93C5FD',
      400: '#60A5FA', // --info-light
      500: '#3B82F6', // --info
      600: '#2563EB', // --info-dark
      700: '#1D4ED8',
      800: '#1E40AF',
      900: '#1E3A8A',
      950: '#172554', // --info-dark-ext
    },

    // Yellow/Amber palette from yukit warning colors
    yellow: {
      50: '#FFFBEB', // --warning-lighter-ext
      100: '#FEF3C7',
      200: '#FDE68A',
      300: '#FCD34D',
      400: '#FBBF24', // --warning-light
      500: '#F59E0B', // --warning
      600: '#D97706', // --warning-dark
      700: '#B45309',
      800: '#92400E',
      900: '#78350F',
      950: '#451A03', // --warning-dark-ext
    },

    // Orange palette from yukit error colors (different from primary)
    orange: {
      50: '#FEF3F2', // --error-lighter-ext
      100: '#FEE2E2',
      200: '#FECACA',
      300: '#FCA5A5',
      400: '#F68373', // --error-light
      500: '#ED5A46', // --error
      600: '#DA3D28', // --error-dark
      700: '#B91C1C',
      800: '#991B1B',
      900: '#7F1D1D',
      950: '#44120B', // --error-dark-ext
    },

    // Red palette for badges and primary status indicators
    red: {
      50: '#fef2f2',
      100: '#fee2e2',
      200: '#fecaca',
      300: '#fca5a5',
      400: '#f87171',
      500: '#fc5858',
      600: '#e34545',
      700: '#c53030',
      800: '#9b2c2c',
      900: '#742a2a',
      950: '#450a0a',
    },
  },

  // ============================================================================
  // SEMANTIC TOKENS
  // Contextual tokens from yukit
  // ============================================================================
  semantic: {
    // ---------------------------------------------------------------------------
    // Transition Duration
    // ---------------------------------------------------------------------------
    transitionDuration: '0.2s',

    // ---------------------------------------------------------------------------
    // Focus Ring
    // ---------------------------------------------------------------------------
    focusRing: {
      width: '2px',
      style: 'solid',
      color: '{primary.color}',
      offset: '2px',
      shadow: 'none',
    },

    // ---------------------------------------------------------------------------
    // Disabled State
    // ---------------------------------------------------------------------------
    disabledOpacity: '0.6',

    // ---------------------------------------------------------------------------
    // Icon Size (from yukit icons.scss)
    // ---------------------------------------------------------------------------
    iconSize: '1.25rem', // --yu-icon-size: 20px

    // ---------------------------------------------------------------------------
    // Anchor Link
    // ---------------------------------------------------------------------------
    anchorGutter: '2px',

    // ---------------------------------------------------------------------------
    // Primary Color Palette from yukit colors.scss
    // --primary: #F95A5C
    // ---------------------------------------------------------------------------
    primary: {
      50: '#FFF5F5', // --primary-lighter-ext
      100: '#FFECEC', // --primary-lighter-2
      200: '#FDA8A9', // --primary-lighter
      300: '#FC8B8D', // interpolated
      400: '#FF7173', // --primary-light
      500: '#F95A5C', // --primary (base)
      600: '#F04346', // --primary-dark
      700: '#CA3739', // --primary-darker
      800: '#9D2A2C', // interpolated
      900: '#710d0f', // --primary-dark-ext
      950: '#4A0809', // darker
    },

    // ---------------------------------------------------------------------------
    // Form Field Tokens
    // ---------------------------------------------------------------------------
    formField: {
      paddingX: '0.75rem',
      paddingY: '0.625rem',
      sm: {
        fontSize: '0.875rem',
        paddingX: '0.625rem',
        paddingY: '0.5rem',
      },
      lg: {
        fontSize: '1.125rem',
        paddingX: '0.875rem',
        paddingY: '0.75rem',
      },
      borderRadius: '{border.radius.md}',
      focusRing: {
        width: '{focus.ring.width}',
        style: '{focus.ring.style}',
        color: '{focus.ring.color}',
        offset: '{focus.ring.offset}',
        shadow: '{focus.ring.shadow}',
      },
      transitionDuration: '{transition.duration}',
    },

    // ---------------------------------------------------------------------------
    // List Tokens
    // ---------------------------------------------------------------------------
    list: {
      padding: '0.25rem 0.25rem',
      gap: '2px',
      header: {
        padding: '0.625rem 1rem 0.375rem 1rem',
      },
      option: {
        padding: '0.625rem 0.75rem',
        borderRadius: '{border.radius.sm}',
      },
      optionGroup: {
        padding: '0.625rem 0.75rem',
        fontWeight: '600',
      },
    },

    // ---------------------------------------------------------------------------
    // Content Tokens
    // ---------------------------------------------------------------------------
    content: {
      borderRadius: '{border.radius.md}',
    },

    // ---------------------------------------------------------------------------
    // Mask Tokens
    // ---------------------------------------------------------------------------
    mask: {
      transitionDuration: '0.15s',
    },

    // ---------------------------------------------------------------------------
    // Navigation Tokens
    // ---------------------------------------------------------------------------
    navigation: {
      list: {
        padding: '0.25rem 0.25rem',
        gap: '2px',
      },
      item: {
        padding: '0.625rem 0.75rem',
        borderRadius: '{border.radius.sm}',
        gap: '0.5rem',
      },
      submenuLabel: {
        padding: '0.625rem 0.75rem',
        fontWeight: '600',
      },
      submenuIcon: {
        size: '0.875rem',
      },
    },

    // ---------------------------------------------------------------------------
    // Overlay Tokens with yukit shadows
    // ---------------------------------------------------------------------------
    overlay: {
      select: {
        borderRadius: '{border.radius.md}',
        shadow: '0px 2px 4px 0px rgba(31, 31, 31, 0.06)', // --shadow-low-box-shadow
      },
      popover: {
        borderRadius: '{border.radius.md}',
        padding: '0.75rem',
        shadow: '0px 16px 32px -12px rgba(0, 0, 0, 0.06)', // --shadow-mid-box-shadow
      },
      modal: {
        borderRadius: '{border.radius.xl}',
        padding: '1.5rem',
        shadow: '0px 40px 96px -8px rgba(0, 0, 0, 0.08)', // --shadow-modal-box-shadow
      },
      navigation: {
        shadow: '0px 2px 4px 0px rgba(31, 31, 31, 0.06)', // --shadow-low-box-shadow
      },
    },

    // ---------------------------------------------------------------------------
    // Color Scheme (Light Mode Only) with yukit colors
    // ---------------------------------------------------------------------------
    colorScheme: {
      light: {
        // Surface colors from yukit neutral colors
        surface: {
          0: '#FFFFFF', // --surface
          50: '#FAFAFA', // --surface-accent
          100: '#F5F5F5', // --overlay
          200: '#EBEBEB', // --element-outline
          300: '#DBDBDB', // --element-accent
          400: '#B8B8B8', // --element-disabled
          500: '#8F8F8F', // --element-low
          600: '#666666', // --element-mid
          700: '#4A4A4A', // interpolated
          800: '#333333', // interpolated
          900: '#1F1F1F', // --element-high
          950: '#0D0D0D', // darker
        },
        // Primary color states
        primary: {
          color: '#F95A5C', // --primary
          contrastColor: '#FFFFFF', // --primary-contrast
          hoverColor: '#F04346', // --primary-dark
          activeColor: '#CA3739', // --primary-darker
        },
        // Highlight (selected items)
        highlight: {
          background: '#FFF5F5', // --primary-lighter-ext
          focusBackground: '#FFECEC', // --primary-lighter-2
          color: '#CA3739', // --primary-darker
          focusColor: '#710d0f', // --primary-dark-ext
        },
        // Mask overlay
        mask: {
          background: 'rgba(0, 0, 0, 0.4)',
          color: '{surface.200}',
        },
        // Form field colors
        formField: {
          background: '{surface.0}',
          disabledBackground: '{surface.100}',
          filledBackground: '{surface.50}',
          filledHoverBackground: '{surface.50}',
          filledFocusBackground: '{surface.50}',
          borderColor: '{surface.300}',
          hoverBorderColor: '{primary.color}',
          focusBorderColor: '{primary.color}',
          invalidBorderColor: '#ED5A46', // --error
          color: '{surface.900}',
          disabledColor: '{surface.400}',
          placeholderColor: '{surface.500}',
          invalidPlaceholderColor: '#DA3D28', // --error-dark
          floatLabelColor: '{surface.500}',
          floatLabelFocusColor: '{primary.color}',
          floatLabelActiveColor: '{surface.600}',
          floatLabelInvalidColor: '#DA3D28', // --error-dark
          iconColor: '{surface.500}',
          shadow: 'none',
        },
        // Text colors
        text: {
          color: '#1F1F1F', // --element-high
          hoverColor: '#1F1F1F',
          mutedColor: '#666666', // --element-mid
          hoverMutedColor: '#4A4A4A',
        },
        // Content area colors
        content: {
          background: '{surface.0}',
          hoverBackground: '{surface.50}',
          borderColor: '{surface.200}',
          color: '{text.color}',
          hoverColor: '{text.hover.color}',
        },
        // Overlay colors
        overlay: {
          select: {
            background: '{surface.0}',
            borderColor: '{surface.200}',
            color: '{text.color}',
          },
          popover: {
            background: '{surface.0}',
            borderColor: '{surface.200}',
            color: '{text.color}',
          },
          modal: {
            background: '{surface.0}',
            borderColor: '{surface.200}',
            color: '{text.color}',
          },
        },
        // List component colors
        list: {
          option: {
            focusBackground: '{surface.100}',
            selectedBackground: '{highlight.background}',
            selectedFocusBackground: '{highlight.focus.background}',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            selectedColor: '{highlight.color}',
            selectedFocusColor: '{highlight.focus.color}',
            icon: {
              color: '{surface.500}',
              focusColor: '{surface.600}',
            },
          },
          optionGroup: {
            background: 'transparent',
            color: '{text.muted.color}',
          },
        },
        // Navigation colors
        navigation: {
          item: {
            focusBackground: '{surface.100}',
            activeBackground: '{surface.100}',
            color: '{text.color}',
            focusColor: '{text.hover.color}',
            activeColor: '{text.hover.color}',
            icon: {
              color: '{surface.500}',
              focusColor: '{surface.600}',
              activeColor: '{surface.600}',
            },
          },
          submenuLabel: {
            background: 'transparent',
            color: '{text.muted.color}',
          },
          submenuIcon: {
            color: '{surface.500}',
            focusColor: '{surface.600}',
            activeColor: '{surface.600}',
          },
        },
      },
    },
  },
});

/**
 * Theme options for PrimeNG configuration
 * darkModeSelector: false ensures only light mode is used
 */
export const yurestThemeOptions = {
  prefix: 'p',
  darkModeSelector: false as const,
  cssLayer: false,
};
